import { type Entity, type FilterValues, type WhereFilterOp } from "@firecms/core";

/**
 * The filters a text search silently drops, applied back on the results.
 *
 * **The bug this exists for.** `useDataSourceTableController` passes its
 * `filter` to `listenCollection` whether or not a search string is set, and
 * seeds that filter from `forceFilter` — so a reference property's
 * `forceFilter` really does reach the data source while an editor is
 * searching. The Firestore delegate is what loses it: `listenCollection`
 * branches into `performTextSearch` *before* it builds a query, and that branch
 * takes a path and a search string and nothing else. Every filter on the
 * collection is discarded for as long as the search box has text in it.
 *
 * Concretely, in the news article's tag picker: `forceFilter: { retired:
 * ["==", false] }` holds while an editor scrolls and stops holding the moment
 * they type. `App.tsx` wraps the delegate with this to close that, which is
 * also what lets `searchTags` return retired tags — the listing view wants to
 * find them, and only the picker wants them gone.
 *
 * Client-side is the only place this can be done: the search branch returns ids
 * from an index, not a query anyone can add a `where` to.
 */

const isArray = (value: unknown): value is unknown[] => Array.isArray(value);

/**
 * One `[op, value]` clause against one stored value.
 *
 * Comparisons use JavaScript's operators rather than Firestore's ordering, and
 * the two differ across mixed types — `null < 1` is false here and true there.
 * That is knowingly out of scope: a filter comparing a field that holds mixed
 * types is not something this CMS's collections can express.
 */
const satisfies = (op: WhereFilterOp, stored: unknown, expected: unknown) => {
    switch (op) {
        case "==":
            return stored === expected;
        case "!=":
            return stored !== expected;
        case "<":
            return (stored as number) < (expected as number);
        case "<=":
            return (stored as number) <= (expected as number);
        case ">":
            return (stored as number) > (expected as number);
        case ">=":
            return (stored as number) >= (expected as number);
        case "in":
            return isArray(expected) && expected.includes(stored);
        case "not-in":
            return isArray(expected) && !expected.includes(stored);
        case "array-contains":
            return isArray(stored) && stored.includes(expected);
        case "array-contains-any":
            return (
                isArray(stored) &&
                isArray(expected) &&
                expected.some((value) => stored.includes(value))
            );
        default:
            /**
             * Fails open, on purpose. An operator this does not implement means
             * FireCMS has grown one, and the two possible wrong answers are not
             * symmetrical: keeping the entity degrades to the behaviour we have
             * shipped until now, while dropping it empties a table with no
             * error and no way for an editor to tell why.
             */
            console.warn(
                `Unsupported filter operator "${op}" in search results; not applied.`,
            );
            return true;
    }
};

/**
 * A predicate over search results for `filter`.
 *
 * An entity missing the filtered field is judged on `undefined`, which is what
 * a Firestore query does too: a document with no `retired` field does not match
 * `retired == false` and is dropped.
 */
export const matchesFilter =
    <M extends Record<string, unknown>>(filter: FilterValues<string>) =>
    (entity: Entity<M>) =>
        Object.entries(filter).every(([key, clause]) => {
            if (!clause) return true;
            const [op, expected] = clause as [WhereFilterOp, unknown];
            return satisfies(op, entity.values?.[key], expected);
        });
